import { useState } from "react";
import {
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import WhiteBgInput from "../WhiteBgInput";
import { creativeDestinationLinkError } from "../utils/creativeDestinationLink";

type CreativeEventLinkFieldProps = {
  link?: string;
  isDisabled?: boolean;
  onChange: (link: string) => void;
};

const eventLinkBoxProps = {
  mt: 4,
  px: 4,
  py: 3,
  borderWidth: "1px",
  borderStyle: "solid" as const,
  borderColor: "gray.200",
  borderRadius: "lg",
};

const linkActionButtonProps = {
  variant: "link" as const,
  fontSize: "sm",
  fontWeight: "medium",
  color: "blue.600",
  minW: "auto",
  _hover: { textDecoration: "underline" },
};

/** Default event link, inline edit, or a saved custom destination. */
export default function CreativeEventLinkField({
  link,
  isDisabled = false,
  onChange,
}: CreativeEventLinkFieldProps) {
  const saved = link?.trim() ?? "";
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(saved);
  const [showError, setShowError] = useState(false);
  const draftError = creativeDestinationLinkError(draft);

  const startEdit = () => {
    setDraft(saved);
    setShowError(false);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setDraft(saved);
    setShowError(false);
    setIsEditing(false);
  };

  const saveEdit = () => {
    if (draftError) {
      setShowError(true);
      return;
    }
    onChange(draft.trim());
    setShowError(false);
    setIsEditing(false);
  };

  const resetLink = () => {
    onChange("");
    setDraft("");
    setShowError(false);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <FormControl
        {...eventLinkBoxProps}
        isInvalid={showError && Boolean(draftError)}
      >
        <HStack justify="space-between" align="center" spacing={3} mb={2}>
          <HStack spacing={2} align="baseline" minW={0} flexWrap="wrap">
            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
              Link
            </Text>
            <Text fontSize="xs" color="gray.500">
              Link clicks will open this page instead
            </Text>
          </HStack>
          <HStack spacing={3} flexShrink={0}>
            <Button
              variant="link"
              fontSize="sm"
              fontWeight="medium"
              color="gray.600"
              minW="auto"
              onClick={cancelEdit}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              bg="gray.800"
              color="white"
              fontWeight="medium"
              _hover={{ bg: "gray.700" }}
              onClick={saveEdit}
            >
              Save
            </Button>
          </HStack>
        </HStack>
        <WhiteBgInput
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            setShowError(false);
          }}
          placeholder="https://"
          autoFocus
        />
        <FormErrorMessage>{draftError}</FormErrorMessage>
      </FormControl>
    );
  }

  if (saved) {
    return (
      <VStack {...eventLinkBoxProps} align="stretch" spacing={1}>
        <HStack justify="space-between" align="center" spacing={3}>
          <Text fontSize="sm" fontWeight="semibold" color="gray.800">
            Link
          </Text>
          <HStack spacing={3} flexShrink={0}>
            <Button
              {...linkActionButtonProps}
              color="gray.600"
              _hover={{ textDecoration: "none" }}
              isDisabled={isDisabled}
              onClick={resetLink}
            >
              Reset
            </Button>
            <Button
              {...linkActionButtonProps}
              isDisabled={isDisabled}
              onClick={startEdit}
            >
              Edit
            </Button>
          </HStack>
        </HStack>
        <Text fontSize="sm" color="gray.700" noOfLines={2}>
          {saved}
        </Text>
      </VStack>
    );
  }

  return (
    <HStack
      {...eventLinkBoxProps}
      justify="space-between"
      align="center"
      spacing={3}
    >
      <HStack spacing={2} align="baseline" minW={0} flexWrap="wrap">
        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
          Link
        </Text>
        <Text fontSize="xs" color="gray.500">
          This defaults to the event page
        </Text>
      </HStack>
      <Button
        {...linkActionButtonProps}
        flexShrink={0}
        isDisabled={isDisabled}
        onClick={startEdit}
      >
        Edit
      </Button>
    </HStack>
  );
}
